import { FormEvent, useState } from 'react';
import { Wine } from '../types';

interface AddScoreModalProps {
  wine: Wine;
  cellarLogRating: number | null;
  onSubmit: (criticName: string, score: number) => void;
  onDelete: (scoreId: string) => void;
  onUpdateMyRating: (rating: number | null) => void;
  onClose: () => void;
}

const COMMON_CRITICS = [
  'Vivino',
  'Wine Spectator',
  'Robert Parker',
  'Wine Advocate',
  'James Suckling',
  'Wine Enthusiast',
  'Vinous',
  'Decanter',
  'Jancis Robinson',
  'CellarTracker'
];

export function AddScoreModal({ wine, cellarLogRating, onSubmit, onDelete, onUpdateMyRating, onClose }: AddScoreModalProps) {
  const [criticName, setCriticName] = useState('');
  const [score, setScore] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const [myRating, setMyRating] = useState(wine.my_rating ? String(wine.my_rating) : '');
  const [editingScoreId, setEditingScoreId] = useState<string | null>(null);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!criticName || !score) return;
    
    const scoreNum = parseFloat(score);
    if (isNaN(scoreNum)) return;
    
    onSubmit(criticName, scoreNum);
    setCriticName('');
    setScore('');
    setShowCustom(false);
    setEditingScoreId(null);
  };

  const handleMyRatingChange = () => {
    const rating = myRating ? parseInt(myRating) : null;
    if (rating !== null && (rating < 1 || rating > 100)) return;
    onUpdateMyRating(rating);
  };

  const handleQuickSelect = (critic: string) => {
    setCriticName(critic);
    const existingScore = wine.critic_scores?.find(s => s.critic_name === critic);
    if (existingScore) {
      setScore(String(existingScore.score));
      setEditingScoreId(existingScore.id);
    } else {
      setScore('');
      setEditingScoreId(null);
    }
    document.getElementById('score-input')?.focus();
  };

  const handleEditScore = (scoreToEdit: { id: string; critic_name: string; score: number }) => {
    setCriticName(scoreToEdit.critic_name);
    setScore(String(scoreToEdit.score));
    setEditingScoreId(scoreToEdit.id);
    if (!COMMON_CRITICS.includes(scoreToEdit.critic_name)) {
      setShowCustom(true);
    }
    document.getElementById('score-input')?.focus();
  };

  const isVivino = criticName === 'Vivino';
  const scoreMin = isVivino ? 1 : 50;
  const scoreMax = isVivino ? 5 : 100;
  const scoreStep = isVivino ? 0.1 : 1;
  const scorePlaceholder = isVivino ? '1-5' : '50-100';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="ios-sheet w-full"
        style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="ios-sheet-handle" />

        {/* Header */}
        <div style={{ 
          padding: '0 20px 12px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ width: '50px' }} />
          <h2 className="wine-title" style={{ 
            fontSize: '18px', 
            margin: 0,
            color: 'var(--color-text)'
          }}>
            Ratings
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-wine)',
              fontSize: '16px',
              cursor: 'pointer'
            }}
          >
            Done
          </button>
        </div>

        {/* Wine Info */}
        <div className="ios-card" style={{ margin: '0 16px 16px', padding: '12px' }}>
          <p className="wine-title" style={{ fontSize: '16px', color: 'var(--color-text)' }}>
            {wine.wine_name}
          </p>
          <p style={{ fontSize: '14px', color: 'var(--color-text-muted)' }}>
            {wine.producer} {wine.vintage && `· ${wine.vintage}`}
          </p>
        </div>

        {/* Scrollable Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px' }}>
          {/* My Rating Section */}
          <div className="ios-card" style={{ padding: '16px', marginBottom: '16px', background: '#ecfdf5' }}>
            <label className="ios-label" style={{ color: 'var(--color-success)' }}>My Rating</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
              <input
                type="number"
                value={myRating}
                onChange={(e) => setMyRating(e.target.value)}
                className="ios-input"
                style={{ flex: 1, borderColor: '#a7f3d0' }}
                min="1"
                max="100"
                placeholder="1-100"
              />
              <button
                type="button"
                onClick={handleMyRatingChange}
                className="ios-btn"
                style={{ background: 'var(--color-success)', color: 'white' }}
              >
                Save
              </button>
            </div>
            <p style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
              Your personal score (1-100 points)
            </p>
            {cellarLogRating !== null && (
              <p style={{ fontSize: '12px', color: 'var(--color-success)', marginTop: '8px' }}>
                CellarLog Community: <strong>{cellarLogRating.toFixed(1)}</strong>
              </p>
            )}
          </div>

          {/* Existing Critic Scores */}
          {wine.critic_scores && wine.critic_scores.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <label className="ios-label">Critic Scores</label>
              <div className="ios-card" style={{ overflow: 'hidden' }}>
                {wine.critic_scores.map((scoreItem, index) => (
                  <div 
                    key={scoreItem.id}
                    style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px',
                      background: editingScoreId === scoreItem.id ? '#fef3c7' : 'transparent',
                      borderBottom: index < wine.critic_scores!.length - 1 ? '1px solid var(--color-border)' : 'none'
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => handleEditScore(scoreItem)}
                      style={{ 
                        background: 'none', 
                        border: 'none', 
                        textAlign: 'left',
                        cursor: 'pointer',
                        flex: 1
                      }}
                    >
                      <span style={{ color: 'var(--color-text)' }}>
                        {scoreItem.critic_name}
                      </span>
                      <span style={{ color: 'var(--color-wine)', fontWeight: '600', marginLeft: '8px' }}>
                        {scoreItem.critic_name === 'Vivino' ? scoreItem.score.toFixed(1) : scoreItem.score}
                        {scoreItem.critic_name === 'Vivino' && ' ★'}
                      </span>
                    </button>
                    <button
                      onClick={() => onDelete(scoreItem.id)}
                      style={{
                        background: '#fee2e2',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        color: '#991b1b',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add Critic Score */}
          <form onSubmit={handleSubmit}>
            <label className="ios-label">{editingScoreId ? 'Edit Score' : 'Add Critic Score'}</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
              {COMMON_CRITICS.map(critic => {
                const hasScore = wine.critic_scores?.some(s => s.critic_name === critic);
                return (
                  <button
                    key={critic}
                    type="button"
                    onClick={() => handleQuickSelect(critic)}
                    className={`ios-pill ${criticName === critic ? 'active' : ''}`}
                    style={hasScore && criticName !== critic ? { background: '#dbeafe', borderColor: '#93c5fd' } : {}}
                  >
                    {critic} {hasScore && '✓'}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => setShowCustom(!showCustom)}
                className={`ios-pill ${showCustom ? 'active' : ''}`}
              >
                Other...
              </button>
            </div>
            
            {showCustom && (
              <input
                type="text"
                value={criticName}
                onChange={(e) => setCriticName(e.target.value)}
                className="ios-input"
                style={{ marginBottom: '12px' }}
                placeholder="Enter critic name"
              />
            )}
            
            {criticName && (
              <div style={{ marginBottom: '16px' }}>
                <label className="ios-label">
                  Score {isVivino ? '(1-5)' : '(50-100)'}
                </label>
                <input
                  id="score-input"
                  type="number"
                  value={score}
                  onChange={(e) => setScore(e.target.value)}
                  className="ios-input"
                  min={scoreMin}
                  max={scoreMax}
                  step={scoreStep}
                  placeholder={scorePlaceholder}
                  required
                />
              </div>
            )}
            
            {criticName && score && (
              <button
                type="submit"
                className="ios-btn w-full"
                style={{ 
                  background: editingScoreId ? '#f59e0b' : 'var(--color-wine)', 
                  color: 'white',
                  marginBottom: '16px'
                }}
              >
                {editingScoreId ? 'Update Score' : 'Add Score'}
              </button>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
